import { useState, useEffect } from 'react';
import { feedbackTypes } from '../constants/general';
import { useFetchSubmissions } from '../hooks/useFetchSubmissions';
import { SectionLoader } from './general/Loaders';
import { Bug, Lightbulb, MessageSquare } from 'lucide-react';
import clsx from 'clsx';

export const Analytics = () => {
  const [submissionsPerProject, setSubmissionsPerProject] = useState([]);

  const { submissions, isLoading } = useFetchSubmissions();

  useEffect(() => {
    if (submissions && submissions.length) {
      const consolidated = {};

      submissions.forEach(item => {
        const name = item.name;

        if (!consolidated[name]) {
          consolidated[name] = {
            name: name,
            total: 0,
            bugs: 0,
            features: 0,
            other: 0,
          };
        }

        consolidated[name].total++;

        if (item.feedback_type === 'bug') {
          consolidated[name].bugs++;
        } else if (item.feedback_type === 'feature') {
          consolidated[name].features++;
        } else {
          consolidated[name].other++;
        }
      });

      const sortedConsolidated = Object.values(consolidated).sort((a, b) => b.total - a.total);

      setSubmissionsPerProject(sortedConsolidated);
    }
  }, [submissions]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 my-12">
        <SectionLoader dotColor="slate" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="bg-gradient-to-br from-cyan-50 to-white pt-12">
        <div className="flex justify-center mb-12">
          <h1 className="text-4xl md:text-5xl font-light text-black">Feedback statistics</h1>
        </div>

        <div className="flex flex-col items-center bg-gradient-to-br from-cyan-300 to-cyan-600 *:text-white rounded-md p-8 mb-12">
          <h2 className="text-6xl font-light mb-2">{submissions?.length}</h2>
          <p className="text-xl font-light">Total Submissions</p>
        </div>

        {submissions && submissions.length ? (
          <div className="container mx-auto px-6">
            <div className="flex flex-col gap-8 mb-8">
              {/* By Feedback Type */}
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <h3 className="text-2xl font-light text-black mb-6">By Feedback Type</h3>
                <div className="space-y-4">
                  {feedbackTypes.map(type => {
                    const count = submissions.filter(
                      sub => sub.feedback_type === type.value,
                    ).length;
                    const percentage =
                      submissions.length > 0 ? ((count / submissions.length) * 100).toFixed(0) : 0;
                    const Icon = type.icon;

                    return (
                      <div key={type.value} className="flex items-center gap-4">
                        <Icon className={clsx('w-6 h-6', type.color)} />
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-700">{type.label}</span>
                            <span className="text-black font-medium">{count}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-cyan-500 h-2 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By Project */}
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <h3 className="text-2xl font-light text-gray-900 mb-6">By Project</h3>
                <div className="space-y-4 max-h-[600px] overflow-y-auto scrollbar pr-2">
                  {submissionsPerProject &&
                    submissionsPerProject?.map((project, index) => (
                      <div
                        key={project.name}
                        className={clsx(
                          'bg-gray-50 rounded-lg p-5 border border-gray-100',
                          index !== submissionsPerProject.length - 1 && 'mb-4',
                        )}
                      >
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                          <h4 className="text-lg font-medium text-gray-900">{project.name}</h4>
                          <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm font-semibold">
                            {project.total} total
                          </span>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-red-50 rounded-lg flex-shrink-0">
                              <Bug className="w-5 h-5 text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-1.5">
                                <span className="text-sm font-medium text-gray-700">Bugs</span>
                                <span className="text-sm font-semibold text-gray-900">
                                  {project.bugs}
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-red-500 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${project.total > 0 ? (project.bugs / project.total) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-blue-50 rounded-lg flex-shrink-0">
                              <Lightbulb className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-1.5">
                                <span className="text-sm font-medium text-gray-700">
                                  Feature Requests
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                  {project.features}
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${project.total > 0 ? (project.features / project.total) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-lg flex-shrink-0">
                              <MessageSquare className="w-5 h-5 text-gray-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-center mb-1.5">
                                <span className="text-sm font-medium text-gray-700">Other</span>
                                <span className="text-sm font-semibold text-gray-900">
                                  {project.other}
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-gray-500 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${project.total > 0 ? (project.other / project.total) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
